import React, { useEffect, useRef, useState } from 'react';
import api from '../../api';
import { Camera, Shield, AlertTriangle, Maximize, EyeOff, Users, UserX, XCircle } from 'lucide-react';
import {
  postProctorReport,
  queueProctorReport,
  flushQueuedProctorReports,
} from '../../utils/proctorReportQueue';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Retry a disqualification report before giving up. A save that fails here is
 * queued locally instead of being dropped, so faculty always receive the report.
 */
const retryProctorReport = async (report, attempts = 3) => {
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    await sleep(700 * attempt);
    try {
      const res = await postProctorReport(report);
      return res.data;
    } catch (err) {
      console.warn(`Proctoring report retry ${attempt}/${attempts} failed:`, err);
    }
  }
  return null;
};


const ProctoringCam = ({
  assessmentTitle = "AI Technical Competency Assessment",
  isActive = true,
  onViolation = () => {},
  onDisqualified = () => {}
}) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [streamActive, setStreamActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [violations, setViolations] = useState([]);
  const [tabSwitchCount, setTabSwitchCount] = useState(0);
  const [warningPopup, setWarningPopup] = useState(null);
  const [isDisqualifiedState, setIsDisqualifiedState] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(!!(document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement));

  const violationsRef = useRef([]);
  const tabSwitchViolationsRef = useRef(0);
  const lastViolationTimeRef = useRef(0);
  const isProcessingFrameRef = useRef(false);

  const requestFullscreenMode = async () => {
    const elem = document.documentElement;
    try {
      if (elem.requestFullscreen) {
        await elem.requestFullscreen();
      } else if (elem.webkitRequestFullscreen) {
        await elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) {
        await elem.msRequestFullscreen();
      }
      setIsFullscreen(true);
    } catch (err) {
      console.warn("Fullscreen request error:", err);
    }
  };

  // 1. Initialize Webcam & Request Fullscreen
  useEffect(() => {
    if (!isActive || isDisqualifiedState) return;

    // Enforce fullscreen on start
    requestFullscreenMode();

    // Access Webcam
    const startCamera = async () => {
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { width: 640, height: 480, frameRate: { ideal: 15, max: 30 } },
          audio: false
        });
        streamRef.current = mediaStream;
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
        setStreamActive(true);
        setCameraError(null);
      } catch (err) {
        console.error("Camera access error:", err);
        setCameraError("Webcam access is required for AI Proctoring. Please allow camera permissions.");
        registerViolation("NO_CAMERA_ACCESS", "Webcam access disabled or unavailable!");
      }
    };

    startCamera();

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [isActive, isDisqualifiedState]);

  // 2. Register Violation & Trigger Warning Alert
  const registerViolation = (type, message) => {
    const now = Date.now();
    // Throttle duplicate violations within 1.2 seconds for real-time responsiveness
    if (now - lastViolationTimeRef.current < 1200) return;
    lastViolationTimeRef.current = now;

    // Capture screenshot snapshot from canvas
    let snapshot = null;
    try {
      if (canvasRef.current && videoRef.current && streamActive) {
        const cvs = canvasRef.current;
        const ctx = cvs.getContext('2d');
        cvs.width = 320;
        cvs.height = 240;
        ctx.drawImage(videoRef.current, 0, 0, cvs.width, cvs.height);
        snapshot = cvs.toDataURL('image/jpeg', 0.6);
      }
    } catch (e) {
      console.warn("Snapshot capture error:", e);
    }

    const isTabSwitch = (type === 'EXITED_FULLSCREEN' || type === 'TAB_OR_APP_SWITCH' || type === 'PAGE_LEAVE');
    let currentTabSwitchCount = tabSwitchViolationsRef.current;

    if (isTabSwitch) {
      currentTabSwitchCount += 1;
      tabSwitchViolationsRef.current = currentTabSwitchCount;
      setTabSwitchCount(currentTabSwitchCount);
    }

    const violationObj = {
      type,
      message,
      isTabSwitch,
      timestamp: new Date().toLocaleTimeString(),
      isoTimestamp: new Date().toISOString(),
      snapshot
    };

    const newViolations = [...violationsRef.current, violationObj];
    violationsRef.current = newViolations;
    setViolations(newViolations);

    // Call external callback
    onViolation(violationObj, newViolations.length);

    // Show warning banner popup
    setWarningPopup({
      type,
      message,
      isTabSwitch,
      count: isTabSwitch ? currentTabSwitchCount : newViolations.length
    });

    // Automatically hide warning popup after 4 seconds if not disqualified
    setTimeout(() => {
      setWarningPopup(prev => (prev && prev.message === message ? null : prev));
    }, 4000);

    // ONLY terminate assessment if student commits MORE THAN 2 TAB-SWITCH / FULLSCREEN violations (>2)
    if (isTabSwitch && currentTabSwitchCount > 2 && !isDisqualifiedState) {
      triggerDisqualification(newViolations);
    }
  };

  // 3. Handle Disqualification
  const triggerDisqualification = async (allViolations) => {
    setIsDisqualifiedState(true);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }

    const report = {
      assessment_title: assessmentTitle,
      violation_count: allViolations.length,
      violations_data: allViolations,
      report_summary: `Disqualified automatically due to ${allViolations.length} proctoring violations recorded during the assessment.`
    };

    // Previously a failed save only produced a console warning while the student
    // still saw the disqualified screen, so the incident never reached faculty.
    // Retry it, then hand an undeliverable report to the durable queue.
    try {
      const res = await postProctorReport(report);
      onDisqualified(allViolations, res.data);
    } catch (err) {
      console.error("Disqualification report save error:", err);
      const retried = await retryProctorReport(report);
      if (retried) {
        onDisqualified(allViolations, retried);
      } else {
        const queued = queueProctorReport(report);
        console.warn(`Proctoring report queued for retry (${queued} pending).`);
        onDisqualified(allViolations, null);
      }
    }
  };

  // Replay any reports a previous session could not deliver.
  useEffect(() => {
    flushQueuedProctorReports();
  }, []);

  // 4. Browser Event Listeners (Tab switch, Fullscreen exit, Blur)
  useEffect(() => {
    if (!isActive || isDisqualifiedState) return;

    const handleFullscreenChange = () => {
      const activeFS = !!(document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement);
      setIsFullscreen(activeFS);
      if (!activeFS && !isDisqualifiedState) {
        registerViolation("EXITED_FULLSCREEN", "Exited full-screen mode!");
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        registerViolation("TAB_OR_APP_SWITCH", "Switching browser tabs or applications detected!");
      }
    };

    const handleWindowBlur = () => {
      registerViolation("TAB_OR_APP_SWITCH", "Browser lost focus or switched application!");
    };

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "Leaving assessment page will result in immediate disqualification!";
      return e.returnValue;
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isActive, isDisqualifiedState, streamActive]);

  // 5. Continuous AI Frame Analysis via Backend Endpoint (1000ms interval)
  useEffect(() => {
    if (!isActive || isDisqualifiedState || !streamActive) return;

    const intervalId = setInterval(async () => {
      if (isProcessingFrameRef.current) return;
      if (!videoRef.current || !canvasRef.current) return;

      isProcessingFrameRef.current = true;
      try {
        const cvs = canvasRef.current;
        const ctx = cvs.getContext('2d');
        cvs.width = 320;
        cvs.height = 240;
        ctx.drawImage(videoRef.current, 0, 0, cvs.width, cvs.height);
        const frameBase64 = cvs.toDataURL('image/jpeg', 0.5);

        const res = await api.post('/proctoring/analyze-frame', { frame: frameBase64 });
        if (res.data && res.data.violation_detected) {
          registerViolation(res.data.violation_type || "AI_DETECTION_VIOLATION", res.data.message || "Proctoring violation detected by AI!");
        }
      } catch (err) {
        console.warn("Proctoring frame analyze request error:", err);
      } finally {
        isProcessingFrameRef.current = false;
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [isActive, isDisqualifiedState, streamActive]);


  const count = violations.length;

  return (
    <div style={{ position: 'relative' }}>
      {/* Hidden processing canvas */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {/* Mandatory Fullscreen Backdrop Overlay when not in Fullscreen */}
      {!isFullscreen && !isDisqualifiedState && (
        <div style={{
          position: 'fixed',
          inset: 0,
          zIndex: 999999,
          background: 'rgba(15, 23, 42, 0.96)',
          backdropFilter: 'blur(12px)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#ffffff',
          padding: '24px',
          textAlign: 'center',
          fontFamily: "'Inter', sans-serif"
        }}>
          <div style={{
            width: '84px', height: '84px', borderRadius: '50%', background: '#eff6ff',
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2563eb', marginBottom: '20px',
            boxShadow: '0 0 30px rgba(37, 99, 235, 0.3)'
          }}>
            <Maximize size={42} />
          </div>
          <h2 style={{ fontSize: '28px', fontWeight: '800', margin: '0 0 12px 0', letterSpacing: '-0.5px' }}>
            🔒 Full-Screen Proctoring Mode Required
          </h2>
          <p style={{ fontSize: '15px', color: '#94a3b8', maxWidth: '520px', lineHeight: '1.6', margin: '0 0 28px 0' }}>
            This technical assessment must run in <strong>True Full-Screen (Kiosk) Mode</strong>.
            Browser tabs, address bar, and operating system taskbars will be hidden throughout the exam.
          </p>
          <button
            onClick={requestFullscreenMode}
            style={{
              padding: '16px 36px',
              background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '700',
              cursor: 'pointer',
              boxShadow: '0 10px 25px rgba(37, 99, 235, 0.4)',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              transition: 'transform 0.15s ease'
            }}
          >
            <Maximize size={20} /> Enable Full-Screen &amp; Begin Assessment
          </button>
        </div>
      )}

      {/* Floating Proctoring Monitor Box */}
      <div style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 99999,
        width: '210px',
        background: '#0f172a',
        borderRadius: '14px',
        border: count === 0 ? '2px solid #3b82f6' : count === 1 ? '2px solid #eab308' : '2px solid #ef4444',
        boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
        overflow: 'hidden',
        fontFamily: "'Inter', sans-serif"
      }}>
        {/* Top Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 12px', background: 'rgba(255,255,255,0.06)',
          borderBottom: '1px solid rgba(255,255,255,0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#38bdf8', fontSize: '11px', fontWeight: '800' }}>
            <Camera size={14} className="spin-slow" /> AI PROCTOR LIVE
          </div>
          <div style={{
            fontSize: '11px', fontWeight: '800', padding: '2px 8px', borderRadius: '12px',
            background: tabSwitchCount === 0 ? '#1e3a8a' : tabSwitchCount === 1 ? '#854d0e' : '#991b1b',
            color: tabSwitchCount === 0 ? '#93c5fd' : tabSwitchCount === 1 ? '#fef08a' : '#fca5a5'
          }}>
            {tabSwitchCount}/2 Tab Switches
          </div>
        </div>

        {/* Video Frame Preview */}
        <div style={{ width: '100%', height: '125px', background: '#000', position: 'relative' }}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          {cameraError && (
            <div style={{
              position: 'absolute', inset: 0, background: 'rgba(15, 23, 42, 0.95)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              padding: '10px', textAlign: 'center', color: '#f87171', fontSize: '11px'
            }}>
              <AlertTriangle size={20} style={{ marginBottom: '4px' }} />
              {cameraError}
            </div>
          )}

          {/* Re-enter Fullscreen Button if exited */}
          {!isFullscreen && (
            <button
              onClick={requestFullscreenMode}
              style={{
                position: 'absolute', bottom: '6px', left: '50%', transform: 'translateX(-50%)',
                background: '#dc2626', color: '#fff', border: 'none', borderRadius: '6px',
                padding: '4px 10px', fontSize: '10px', fontWeight: '800', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap'
              }}
            >
              <Maximize size={10} /> Re-enter Fullscreen
            </button>
          )}
        </div>
      </div>

      {/* Violation Pop-up Alert Modal Overlay */}
      {warningPopup && !isDisqualifiedState && (
        <div style={{
          position: 'fixed',
          top: '30px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 999999,
          background: warningPopup.isTabSwitch ? '#7f1d1d' : '#854d0e',
          color: '#ffffff',
          padding: '16px 28px',
          borderRadius: '16px',
          boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
          border: warningPopup.isTabSwitch ? '2px solid #ef4444' : '2px solid #f59e0b',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          animation: 'bounce 0.4s ease-in-out',
          maxWidth: '550px'
        }}>
          <div style={{
            width: '44px', height: '44px', borderRadius: '12px', background: warningPopup.isTabSwitch ? '#dc2626' : '#d97706',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
          }}>
            <Shield size={26} color="#ffffff" />
          </div>
          <div>
            <div style={{ fontSize: '12px', fontWeight: '800', color: warningPopup.isTabSwitch ? '#fca5a5' : '#fef08a', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {warningPopup.isTabSwitch
                ? `⚠️ TAB SWITCH / FULLSCREEN VIOLATION (${warningPopup.count} / 2 Allowed)`
                : `⚠️ AI WEBCAM PROCTORING WARNING`}
            </div>
            <div style={{ fontSize: '15px', fontWeight: '700', margin: '2px 0 4px 0' }}>
              {warningPopup.message}
            </div>
            <div style={{ fontSize: '12px', color: warningPopup.isTabSwitch ? '#fecaca' : '#fef9c3' }}>
              {warningPopup.isTabSwitch
                ? (warningPopup.count > 2
                  ? 'Exceeded 2 tab-switch limits. Automatically terminating assessment...'
                  : `Caution: Switching tabs 3 times will automatically terminate and disqualify your test!`)
                : 'Warning recorded & logged for faculty review. (Note: Only tab-switching will terminate assessment).'}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ProctoringCam;

